import React, { useEffect, useMemo, useState } from "react";
import { Check, Loader2, RefreshCw } from "lucide-react";
import PrivateFontPreviewV2 from "./PrivateFontPreviewV2";
import { contrastRatio } from "../../lib/colorSystemPresentation.mjs";

const readablePaletteText = (background) => contrastRatio(background, "#2B211A") >= contrastRatio(background, "#FFFDF9") ? "#2B211A" : "#FFFDF9";
export const fontFaceKey = (entry) => `${entry.font.family}:${entry.treatment.font_style || "normal"}:${entry.treatment.font_weight || 400}`;

import {
  buildTypographySystems,
  resolveTypographyPreferencesByRole,
  ROLE_LABELS,
  ROLE_ORDER,
} from "./typographySystemBuilderV2";
import {
  createTypographyPreference,
  listFontCatalog,
  updateTypographyPreference,
} from "../../lib/brandStudioApi";

function googleCssUrl(items) {
  const families = [...new Set(items.map((item) => item?.family).filter(Boolean))]
    .slice(0, 48)
    .map((family) => "family=" + encodeURIComponent(family).replace(/%20/g, "+"));
  return families.length
    ? "https://fonts.googleapis.com/css2?" + families.join("&") + "&display=swap"
    : "";
}

export function treatmentStyle(roleEntry, fallback = "serif") {
  const family = roleEntry?.font?.family;
  const treatment = roleEntry?.treatment || {};
  return {
    fontFamily: family ? `"${family}", ${fallback}` : undefined,
    fontWeight: treatment.font_weight || 400,
    fontStyle: treatment.font_style || "normal",
    letterSpacing: `${treatment.letter_spacing_em || 0}em`,
    textTransform: treatment.text_transform || "none",
  };
}

function roleFact(entry) {
  const treatment = entry?.treatment || {};
  const pieces = [
    entry?.font?.family || "—",
    treatment.font_weight ? `насыщенность ${treatment.font_weight}` : null,
    treatment.font_style === "italic" ? "курсив" : null,
    treatment.text_transform === "uppercase" ? "прописные буквы" : null,
    treatment.letter_spacing_em
      ? `межбуквенный интервал ${treatment.letter_spacing_em > 0 ? "+" : ""}${Math.round(treatment.letter_spacing_em * 100)}%`
      : null,
  ].filter(Boolean);
  return pieces.join(" · ");
}

export function SystemSpecimen({ system, brandName, palette, compact = false, showDetails = true, fontStatus = {}, onPrivateStatus }) {
  const signature = system.roles.signature;
  const headline = system.roles.headline;
  const body = system.roles.body;
  const accent = system.roles.accent;

  if (compact) {
    const background = palette?.colors?.find((color) => ["primary_light", "neutral"].includes(color.role))?.hex || "#FFFDF9";
    const preferred = palette?.colors?.find((color) => color.role === "primary_dark")?.hex || "#2B211A";
    // Preview colors come from the approved palette; text stays readable.
    const text = contrastRatio(background, preferred) >= 4.5 ? preferred : readablePaletteText(background);
    return <div className="min-w-0 space-y-2.5 rounded-2xl border border-black/10 p-3 sm:space-y-4 sm:p-4" style={{ backgroundColor: background, color: text }} data-testid={"typography-system-specimen-" + system.id}>
      {["signature", "headline", "body", ...(accent ? ["accent"] : [])].map((role) => {
        const entry = system.roles[role];
        const primary = role === "signature" ? brandName || "Твой бренд" : role === "headline" ? "Красота в деталях" : role === "body" ? "Хороший дизайн помогает увидеть главное. Каждая деталь продолжает характер бренда." : "С любовью к деталям";
        const latin = role === "signature" ? "Beauty in every detail" : role === "headline" ? "Made with care" : role === "body" ? "Thoughtful details create a lasting impression." : "A personal touch";
        const sample = (family) => <div className="break-words" style={{ ...treatmentStyle(entry), ...(family ? { fontFamily: family } : {}) }}>
          <div className={role === "signature" ? "text-[23px] leading-tight sm:text-[28px] md:text-[32px]" : role === "headline" ? "text-[21px] leading-tight sm:text-[24px]" : "text-[13px] leading-5 sm:text-[15px] sm:leading-6"}>{!entry.font.uploaded && !entry.font.scripts?.includes("cyrillic") && /[А-Яа-яЁё]/.test(primary) ? "Beauty in every detail" : primary}</div>
          <div className="mt-0.5 text-[12px] leading-5 sm:mt-1 sm:text-[15px] sm:leading-6">{latin}</div>
        </div>;
        return <div key={role}>
          <div className="mb-1 text-[10px] font-medium sm:text-[11px]">{ROLE_LABELS[role]} · {entry.font.family}{entry.locked ? " · твой выбор" : ""}</div>
          {entry.font.uploaded ? <>
            <PrivateFontPreviewV2 assetId={entry.font.font_asset_id} fileUrl={entry.font.font_asset_url} onStatus={onPrivateStatus}>{sample}</PrivateFontPreviewV2>
            {showDetails && <p className="mt-1 text-[11px]">Права подтверждены тобой. Проверь кириллицу и Latin на образце своего файла.</p>}
          </> : fontStatus[fontFaceKey(entry)] === "ready" ? sample() : <p role="status" className="text-[12px]">{fontStatus[fontFaceKey(entry)] === "error" ? "Шрифт не загрузился. Обнови страницу, чтобы увидеть настоящий образец." : "Загружаю шрифт…"}</p>}
          {!entry.font.uploaded && showDetails && <p className="mt-1 text-[11px]">{entry.font.scripts?.includes("cyrillic") ? "Кириллица + Latin" : "Только Latin — кириллица не поддерживается"} · {entry.font.license_id || "OFL-1.1"} · {entry.treatment.font_style === "italic" ? "курсив" : "прямое начертание"} · {entry.treatment.font_weight}</p>}
        </div>;
      })}
    </div>;
  }

  return (
    <div className="rounded-[18px] border border-[#DCCDB8] bg-[#FFFDF9] p-5 md:p-6" data-testid={"typography-system-specimen-" + system.id}>
      <div className="text-[9px] uppercase tracking-[0.15em] text-[#9A8168]">Фирменное имя</div>
      <div
        className="mt-2 text-[28px] leading-tight text-[#2B211A] md:text-[34px]"
        style={treatmentStyle(signature, "sans-serif")}
      >
        {brandName || "Oksana Anokina"}
      </div>

      <div className="mt-7 text-[9px] uppercase tracking-[0.15em] text-[#9A8168]">Заголовок</div>
      <div
        className="mt-2 max-w-2xl text-[26px] leading-tight text-[#2B211A] md:text-[32px]"
        style={treatmentStyle(headline)}
      >
        Создаём бренд, который узнают
      </div>

      <div className="mt-6 text-[9px] uppercase tracking-[0.15em] text-[#9A8168]">Основной текст</div>
      <p
        className="mt-2 max-w-2xl text-[15px] leading-7 text-[#5D4736]"
        style={treatmentStyle(body)}
      >
        Бренд должен быть ясным, живым и удобным в ежедневной работе. Основной текст держит ритм, читаемость и не спорит с более характерными слоями системы.
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-[14px] border border-[#E5D9C8] px-4 py-3">
          <div className="text-[9px] uppercase tracking-[0.14em] text-[#9A8168]">Цена / служебный текст</div>
          <div className="mt-2 text-[21px] text-[#2B211A]" style={treatmentStyle(body)}>
            129 €
          </div>
          <div className="mt-1 text-[11px] text-[#806B55]" style={treatmentStyle(body)}>
            Индивидуальный заказ
          </div>
        </div>
        <div className="rounded-[14px] border border-[#E5D9C8] px-4 py-3">
          <div className="text-[9px] uppercase tracking-[0.14em] text-[#9A8168]">Акцент</div>
          <div className="mt-2 text-[20px] leading-tight text-[#4A3728]" style={treatmentStyle(accent)}>
            Твой стиль — в каждой детали
          </div>
        </div>
      </div>
    </div>
  );
}

function RoleArchitecture({ system, onChooseRole }) {
  return (
    <div className="mt-4 grid gap-2 sm:grid-cols-2">
      {ROLE_ORDER.map((role) => {
        const entry = system.roles[role];
        return (
          <div key={role} className="rounded-[14px] border border-[#E4D7C5] bg-[#FFFCF7] p-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[9px] uppercase tracking-[0.13em] text-[#927A5B]">
                  {ROLE_LABELS[role]}
                </div>
                <div className="mt-1.5 text-[11.5px] text-[#4A3728]">{roleFact(entry)}</div>
              </div>
              {entry?.locked && (
                <span className="rounded-full bg-[#EEF0DF] px-2 py-1 text-[9px] text-[#586044]">твой выбор</span>
              )}
            </div>
            {entry?.reason && (
              <p className="mt-2 text-[10.5px] leading-4 text-[#806B55]">{entry.reason}</p>
            )}
            {!entry?.locked && (
              <button
                type="button"
                onClick={() => onChooseRole?.(role)}
                className="mt-2 inline-flex items-center gap-1 text-[10.5px] text-[#6B5543] underline underline-offset-4"
              >
                <RefreshCw size={10} /> Выбрать другой
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ProfessionalRules({ evidence }) {
  return (
    <div className="mt-5 rounded-[18px] border border-[#DCCDB8] bg-[#FFFDF9] p-4 md:p-5" data-testid="typography-system-professional-rules">
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#8A6D53]">На чём строится система</div>
      <div className="mt-3 grid gap-4 md:grid-cols-2">
        <div>
          <div className="text-[11px] font-medium text-[#4A3728]">Твои подтверждённые данные</div>
          {evidence.anchor_is_visual_reference && (
            <p className="mt-2 text-[11.5px] leading-5 text-[#655141]">
              Типографический ориентир остаётся визуальным ориентиром. Brand Studio не выдаёт картинку за распознанную гарнитуру.
            </p>
          )}
          {evidence.anchor_note && (
            <p className="mt-2 rounded-[12px] bg-[#F7F1E8] px-3 py-2 text-[11.5px] leading-5 text-[#5D4736]">
              «{evidence.anchor_note}»
            </p>
          )}
          {!!evidence.locked_roles.length && (
            <div className="mt-3 space-y-1.5">
              {evidence.locked_roles.map((item) => (
                <div key={item.role} className="text-[11px] text-[#655141]">
                  • {ROLE_LABELS[item.role]} — {item.family} · уже выбрано тобой
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-[11px] font-medium text-[#4A3728]">Профессиональные правила</div>
          <div className="mt-2 space-y-1.5 text-[11.5px] leading-5 text-[#655141]">
            <div>• Сначала определяется функция роли, потом гарнитура.</div>
            <div>• Одна гарнитура допустима, если иерархию дают насыщенность, курсив, размер и межбуквенный интервал.</div>
            <div>• Вторая гарнитура появляется, когда бренду действительно нужен отдельный характерный или текстовый голос.</div>
            <div>• Третья гарнитура не добавляется автоматически: ей нужна отдельная смысловая задача.</div>
            <div>• Кириллица и реальные носители проверяются до принятия системы.</div>
          </div>
        </div>
      </div>
    </div>
  );
}


function AlternativeSystem({ system, brandName, onChooseRole, onAccept, onCancel, confirming, accepting }) {
  return (
    <article className="mt-4 rounded-[20px] border border-[#D9C8B5] bg-[#FFF9F2] p-4 md:p-5" data-testid={"typography-system-alternative-" + system.id}>
      <div className="text-[9.5px] uppercase tracking-[0.16em] text-[#927A5B]">Альтернатива с заменой подтверждённого решения</div>
      <h5 className="mt-1.5 font-serif text-[22px] text-[#2B211A]">{system.title}</h5>
      <p className="mt-2 text-[12px] leading-5 text-[#655141]">{system.principle}</p>

      <div className="mt-4 rounded-[14px] border border-[#E1CDBA] bg-white px-4 py-3">
        <div className="text-[10px] uppercase tracking-[0.13em] text-[#8A6D53]">Что придётся заменить</div>
        <div className="mt-2 space-y-1.5">
          {(system.replacements || []).map((item) => (
            <p key={item.role} className="text-[11.5px] leading-5 text-[#6B5543]">
              • {item.role_label}: <strong>{item.from}</strong> → {item.to}
            </p>
          ))}
        </div>
        <p className="mt-2 text-[10.5px] leading-4 text-[#8A745E]">
          Ничего не заменится без отдельного подтверждения.
        </p>
      </div>

      <div className="mt-4">
        <SystemSpecimen system={system} brandName={brandName} />
      </div>
      <RoleArchitecture system={system} onChooseRole={onChooseRole} />

      {!confirming ? (
        <button
          type="button"
          onClick={() => onAccept(system, false)}
          className="mt-4 inline-flex min-h-10 items-center rounded-full border border-[#BFA98D] bg-white px-4 text-[11.5px] text-[#5D4736]"
        >
          Рассмотреть замену
        </button>
      ) : (
        <div className="mt-4 rounded-[14px] border border-[#D8BFA8] bg-[#FFFDF9] p-3">
          <p className="text-[11.5px] leading-5 text-[#5D4736]">
            Подтвердить замену указанных шрифтов и сохранить эту альтернативную систему?
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={accepting}
              onClick={() => onAccept(system, true)}
              className="min-h-9 rounded-full border border-[#8B6B4D] bg-[#F3E9DB] px-3.5 text-[11px] text-[#4A3728] disabled:opacity-50"
            >
              {accepting ? "Сохраняю…" : "Да, заменить и сохранить"}
            </button>
            <button
              type="button"
              disabled={accepting}
              onClick={onCancel}
              className="min-h-9 rounded-full border border-[#D8C7AE] bg-white px-3.5 text-[11px] text-[#6B5543] disabled:opacity-50"
            >
              Отмена
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

export default function FontPairingSystemV2({
  preferences = [],
  legacyAnchor = null,
  brandName = "Oksana Anokina",
  onChanged,
  onChooseRole,
}) {
  const [catalog, setCatalog] = useState([]);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [acceptingSystem, setAcceptingSystem] = useState(null);
  const [replacementConfirm, setReplacementConfirm] = useState(null);

  const languageRequirement = useMemo(() => {
    const active = (preferences || []).filter((item) => item.status !== "archived");
    return active.length > 0 && active.every((item) => item.language_requirement === "latin")
      ? "latin"
      : "both";
  }, [preferences]);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    listFontCatalog({ language_requirement: languageRequirement })
      .then((items) => {
        if (cancelled) return;
        setCatalog(items || []);
        setStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setError("Не удалось открыть библиотеку для типографической системы.");
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [languageRequirement]);

  const result = useMemo(
    () => buildTypographySystems({
      catalog,
      preferences,
      legacyAnchor,
      requirement: languageRequirement,
    }),
    [catalog, preferences, legacyAnchor, languageRequirement],
  );

  useEffect(() => {
    const fonts = [...result.systems, ...(result.alternatives || [])].flatMap((system) =>
      ROLE_ORDER.map((role) => system.roles[role]?.font),
    );
    const href = googleCssUrl([...catalog, ...fonts]);
    if (!href) return undefined;
    const id = "brand-studio-typography-system-css";
    let link = document.getElementById(id);
    if (!link) {
      link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    link.href = href;
    return undefined;
  }, [catalog, result.systems, result.alternatives]);

  const activeByRole = useMemo(
    () => resolveTypographyPreferencesByRole(preferences),
    [preferences],
  );

  const acceptSystem = async (system, allowReplacement = false) => {
    if (!system || acceptingSystem) return;
    if (system.requires_replacement && !allowReplacement) {
      setReplacementConfirm(system.id);
      return;
    }
    setAcceptingSystem(system.id);
    setError("");
    try {
      for (const role of ROLE_ORDER) {
        const entry = system.roles[role];
        if (!entry?.font) continue;
        const existing = activeByRole.get(role);
        const treatmentPayload = {
          font_weight: entry.treatment?.font_weight ?? null,
          font_style: entry.treatment?.font_style ?? null,
          letter_spacing_em: entry.treatment?.letter_spacing_em ?? null,
          text_transform: entry.treatment?.text_transform ?? null,
        };
        const newRolePayload = {
          ...treatmentPayload,
          note: `Типографическая система: ${system.title}`,
          language_requirement: languageRequirement,
          origin: "user_explicit",
        };

        if (existing) {
          const isConcreteExisting = existing.mode === "choose_font" || existing.mode === "uploaded_font";
          const existingFamily = existing.font_family || null;
          const proposedFamily = entry.font.family || null;
          if (
            isConcreteExisting &&
            existingFamily &&
            proposedFamily &&
            existingFamily !== proposedFamily &&
            !allowReplacement
          ) {
            throw new Error("locked typography decision");
          }

          if (
            allowReplacement &&
            isConcreteExisting &&
            existingFamily &&
            proposedFamily &&
            existingFamily !== proposedFamily
          ) {
            if (entry.font.id) {
              await updateTypographyPreference(existing.id, {
                ...treatmentPayload,
                mode: "choose_font",
                media_id: null,
                font_catalog_id: entry.font.id,
                font_asset_id: null,
              });
            } else if (entry.font.uploaded && entry.font.font_asset_id) {
              await updateTypographyPreference(existing.id, {
                ...treatmentPayload,
                mode: "uploaded_font",
                media_id: null,
                font_catalog_id: null,
                font_asset_id: entry.font.font_asset_id,
              });
            }
          } else if (existing.mode === "choose_font" && entry.font.id) {
            await updateTypographyPreference(existing.id, {
              ...treatmentPayload,
              mode: "choose_font",
              media_id: null,
              font_catalog_id: entry.font.id,
              font_asset_id: null,
            });
          } else if (existing.mode === "uploaded_font") {
            await updateTypographyPreference(existing.id, treatmentPayload);
          } else if (existing.mode === "provided" && entry.font.id) {
            await createTypographyPreference({
              role,
              mode: "choose_font",
              media_id: null,
              font_catalog_id: entry.font.id,
              font_asset_id: null,
              ...newRolePayload,
            });
          } else if (existing.mode === "provided" && entry.font.uploaded && entry.font.font_asset_id) {
            await createTypographyPreference({
              role,
              mode: "uploaded_font",
              media_id: null,
              font_catalog_id: null,
              font_asset_id: entry.font.font_asset_id,
              ...newRolePayload,
            });
          }
        } else if (entry.font.id) {
          await createTypographyPreference({
            role,
            mode: "choose_font",
            media_id: null,
            font_catalog_id: entry.font.id,
            font_asset_id: null,
            ...newRolePayload,
          });
        } else if (entry.font.uploaded && entry.font.font_asset_id) {
          await createTypographyPreference({
            role,
            mode: "uploaded_font",
            media_id: null,
            font_catalog_id: null,
            font_asset_id: entry.font.font_asset_id,
            ...newRolePayload,
          });
        }
      }
      setReplacementConfirm(null);
      await onChanged?.();
    } catch {
      setError("Не удалось сохранить типографическую систему целиком.");
    } finally {
      setAcceptingSystem(null);
    }
  };

  return (
    <section className="mt-7 rounded-[20px] border border-[#D5C4AD] bg-[#F1E9DD] p-5 md:p-6" data-testid="typography-system-builder">
      <div className="max-w-3xl">
        <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">
          ТИПОГРАФИЧЕСКАЯ СИСТЕМА
        </div>
        <h4 className="mt-2 font-serif text-[25px] leading-tight text-[#2B211A] md:text-[29px]">
          Не пара шрифтов — типографическая система бренда
        </h4>
        <p className="mt-2 text-[12.5px] leading-5 text-[#655141]">
          Я сначала определяю, сколько типографических голосов действительно нужно бренду. Иногда достаточно одной гарнитуры. Иногда нужны две: характер + читаемость. Третий голос появляется только когда у него есть отдельная функция.
        </p>
      </div>

      {status === "loading" && (
        <div className="mt-5 inline-flex items-center gap-2 text-[12px] text-[#806B55]">
          <Loader2 size={14} className="animate-spin" /> Собираю системы и проверяю роли…
        </div>
      )}

      {status !== "loading" && (
        <>
          <ProfessionalRules evidence={result.evidence} />

          <div className="mt-5 space-y-5">
            {result.systems.map((system, index) => (
              <article key={system.id} className="rounded-[20px] border border-[#D6C6B0] bg-[#F9F4EC] p-4 md:p-5" data-testid={"typography-system-option-" + system.id}>
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2 text-[9.5px] uppercase tracking-[0.16em] text-[#927A5B]">
                      <span>Система {index + 1} · {system.family_count} {system.family_count === 1 ? "гарнитура" : system.family_count < 5 ? "гарнитуры" : "гарнитур"}</span>
                      {system.recommended && (
                        <span className="rounded-full bg-[#EEF0DF] px-2 py-1 text-[9px] tracking-normal text-[#586044]">
                          Рекомендую первой
                        </span>
                      )}
                    </div>
                    <h5 className="mt-1.5 font-serif text-[23px] text-[#2B211A]">{system.title}</h5>
                    <p className="mt-1.5 max-w-2xl text-[12px] leading-5 text-[#655141]">{system.principle}</p>
                    <div className="mt-2 text-[10.5px] text-[#8A745E]">
                      {system.families.join(" + ")}
                    </div>
                    <div className="mt-4 grid gap-2 md:grid-cols-2">
                      <div className="rounded-[12px] border border-[#E2D5C2] bg-[#FFFDF9] px-3 py-2">
                        <div className="text-[9px] uppercase tracking-[0.13em] text-[#927A5B]">Почему это может подойти</div>
                        <p className="mt-1.5 text-[11px] leading-4 text-[#655141]">{system.fit_reason}</p>
                      </div>
                      <div className="rounded-[12px] border border-[#E2D5C2] bg-[#FFFDF9] px-3 py-2">
                        <div className="text-[9px] uppercase tracking-[0.13em] text-[#927A5B]">Компромисс</div>
                        <p className="mt-1.5 text-[11px] leading-4 text-[#655141]">{system.tradeoff}</p>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => acceptSystem(system)}
                    disabled={Boolean(acceptingSystem)}
                    className="inline-flex min-h-10 shrink-0 items-center justify-center gap-1.5 rounded-full border border-[#8B6B4D] bg-[#F3E9DB] px-4 text-[11.5px] text-[#4A3728] disabled:opacity-50"
                    data-testid={"typography-system-accept-" + system.id}
                  >
                    <Check size={13} />
                    {acceptingSystem === system.id ? "Сохраняю…" : "Выбрать эту систему"}
                  </button>
                </div>

                <div className="mt-4">
                  <SystemSpecimen system={system} brandName={brandName} />
                </div>
                <RoleArchitecture system={system} onChooseRole={onChooseRole} />
              </article>
            ))}
          </div>

          {!!result.alternatives?.length && (
            <div className="mt-7">
              <div className="text-[10px] uppercase tracking-[0.16em] text-[#8A6D53]">Если захочется пересмотреть уже выбранное</div>
              <p className="mt-1.5 text-[11.5px] leading-5 text-[#806B55]">
                Эти варианты не входят в основные рекомендации, потому что требуют заменить подтверждённый шрифт.
              </p>
              {result.alternatives.map((system) => (
                <AlternativeSystem
                  key={system.id}
                  system={system}
                  brandName={brandName}
                  onChooseRole={onChooseRole}
                  onAccept={acceptSystem}
                  onCancel={() => setReplacementConfirm(null)}
                  confirming={replacementConfirm === system.id}
                  accepting={acceptingSystem === system.id}
                />
              ))}
            </div>
          )}

          {!result.systems.length && (
            <div className="mt-5 rounded-[16px] border border-[#E2CFC8] bg-[#FFF8F4] p-4 text-[12px] text-[#6B443A]">
              Пока не удалось собрать типографическую систему из доступных данных.
            </div>
          )}
        </>
      )}

      {error && (
        <div className="mt-4 rounded-[12px] border border-[#E2CFC8] bg-[#FFF8F4] px-3 py-2 text-[11.5px] text-[#6B443A]">
          {error}
        </div>
      )}
    </section>
  );
}

